import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI, billingAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Shield, CheckCircle, AlertCircle } from 'lucide-react';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { language } = useTranslation();
  const hasRun = useRef(false);

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'success_checkout' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMsg(language === 'fr'
        ? 'Lien de vérification invalide ou manquant.'
        : 'Invalid or missing verification link.');
      return;
    }

    (async () => {
      try {
        // 1. Verify the token with the backend
        await authAPI.verifyEmail(token);

        // 2. Fetch the fresh user context so the app knows the user is now verified
        try {
          const meRes = await authAPI.me();
          setUser(meRes.data);
        } catch {
          // me() may fail if the session cookie expired — that's fine
        }

        const intendedPlanRaw = localStorage.getItem('intended_plan');
        if (intendedPlanRaw) {
          localStorage.removeItem('intended_plan');
          let planId = 'auditor';
          let interval = 'year';
          try {
            const parsed = JSON.parse(intendedPlanRaw);
            if (parsed && typeof parsed.plan === 'string') {
              planId = parsed.plan;
              interval = (parsed.interval === 'month' || parsed.interval === 'year') ? parsed.interval : 'year';
            }
          } catch {
            if (typeof intendedPlanRaw === 'string' && (intendedPlanRaw === 'auditor' || intendedPlanRaw === 'firm')) {
              planId = intendedPlanRaw;
            }
          }
          setStatus('success_checkout');
          const currency = language === 'fr' ? 'eur' : 'usd';
          const res = await billingAPI.checkout(window.location.origin, planId, currency, language, interval);
          if (res.data?.url) {
            window.location.href = res.data.url;
            return;
          }
        }

        setStatus('success');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);

      } catch (err) {
        const detail = err.response?.data?.detail || '';
        setStatus('error');
        setErrorMsg(
          language === 'fr'
            ? 'Lien de vérification invalide ou expiré. Veuillez vous reconnecter pour en recevoir un nouveau.'
            : 'Verification link is invalid or expired. Please log in to receive a new one.'
        );
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <Shield className="w-10 h-10 text-blue-600 mx-auto mb-6" strokeWidth={1.5} />

        {status === 'loading' && (
          <>
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-neutral-400 text-sm font-body">
              {language === 'fr' ? 'Vérification en cours...' : 'Verifying your account...'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl text-white mb-2">
              {language === 'fr' ? 'Email vérifié !' : 'Email verified!'}
            </h1>
            <p className="text-neutral-400 text-sm font-body mb-2">
              {language === 'fr'
                ? 'Votre compte est activé. Redirection vers votre tableau de bord...'
                : 'Your account is activated. Redirecting to your dashboard...'}
            </p>
            <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mt-4" />
          </>
        )}

        {status === 'success_checkout' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl text-white mb-2">
              {language === 'fr' ? 'Email vérifié !' : 'Email verified!'}
            </h1>
            <p className="text-neutral-400 text-sm font-body mb-2">
              {language === 'fr'
                ? 'Redirection vers le paiement sécurisé...'
                : 'Redirecting to secure payment...'}
            </p>
            <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mt-4" />
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl text-white mb-2">
              {language === 'fr' ? 'Lien invalide' : 'Invalid link'}
            </h1>
            <p className="text-neutral-400 text-sm font-body mb-6">{errorMsg}</p>
            <Link
              to="/login"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest px-6 py-3 rounded-sm transition-colors"
            >
              {language === 'fr' ? 'SE CONNECTER' : 'LOG IN'}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
